import type { PropertyValues, ReactiveControllerHost } from 'lit'
import type { InputMixinInterface } from '../common/mixins/InputMixin.js'
import type { NumberFieldChangeReason } from './NumberFieldEvents.js'
import * as addIcon from '@nordhealth/icons/lib/assets/interface-add-small.js'
import * as removeIcon from '@nordhealth/icons/lib/assets/interface-remove-small.js'
import { html, isServer, LitElement, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { createRef, ref } from 'lit/directives/ref.js'

import { FormDataController } from '../common/controllers/FormDataController.js'
import { StateAttributesController } from '../common/controllers/StateAttributesController.js'
import { NordEvent } from '../common/events.js'
import { injectLegacyLightDomStyles } from '../common/light-dom-styles.js'
import { DraftComponentMixin } from '../common/mixins/DraftComponentMixin.js'
import { FieldControlMixin } from '../common/mixins/FieldControlMixin.js'
import { SizeMixin } from '../common/mixins/SizeMixin.js'
import Icon from '../icon/Icon.js'
import { LocalizeController } from '../localization/LocalizeController.js'
import { getCurrencySymbol, resolveCurrencyPlacement } from './currency.js'
import { formatNumber } from './format.js'
import style from './NumberField.css'
import { NumberFieldChangeEvent } from './NumberFieldEvents.js'
import {
  ANY_MINUS_DETECT_RE,
  ANY_PLUS_DETECT_RE,
  ARABIC_DETECT_RE,
  BASE_NON_NUMERIC_SYMBOLS,
  FULLWIDTH_DETECT_RE,
  getNumberLocaleDetails,
  HAN_DETECT_RE,
  MINUS_SIGNS_WITH_ASCII,
  parseNumber,
  PERCENTAGES,
  PERMILLE,
  PERSIAN_DETECT_RE,
  PLUS_SIGNS_WITH_ASCII,
  SPACE_SEPARATOR_RE,
} from './parse.js'
import {
  clamp,
  hasNumberFormatRoundingOptions,
  removeFloatingPointErrors,
  toValidatedNumber,
} from './validate.js'

export { getCurrencySymbol, resolveCurrencyPlacement } from './currency.js'
export { NumberFieldChangeEvent } from './NumberFieldEvents.js'
export type { NumberFieldChangeReason } from './NumberFieldEvents.js'

/**
 * The editable state of the inner input handed to (and returned from) a custom
 * `mask` function: the current text and the caret/selection offsets. The shape
 * mirrors a mask library's element state (e.g. Maskito's `ElementState`), so a
 * mask transformer can be adapted with a thin wrapper.
 */
export interface NumberFieldMaskState {
  value: string
  selectionStart: number
  selectionEnd: number
}

/** Contextual info passed to a custom `mask` function alongside the editable state. */
export interface NumberFieldMaskContext {
  locale?: string | string[]
  format?: Intl.NumberFormatOptions
}

/** Signature of a custom `mask` transformer function. */
export type NumberFieldMaskInput = (
  state: NumberFieldMaskState,
  context: NumberFieldMaskContext,
) => NumberFieldMaskState

Icon.registerIcon(addIcon)
Icon.registerIcon(removeIcon)

/** Press-and-hold timing (ms) and pointer thresholds, ported from Base UI constants. */
const START_AUTO_CHANGE_DELAY = 400
const CHANGE_VALUE_TICK_DELAY = 60
const SCRUB_PIXEL_SENSITIVITY = 2
// Base UI's DEFAULT_STEP (1) is the fallback when getStepAmount() returns
// undefined; here getStepAmount always resolves to a concrete number (step is
// never undefined and 'any' is normalised to 1), so no separate fallback is needed.

/** Per-instance counter so light-DOM ids (input + scrub label `for`) stay unique. */
let numberFieldCount = 1

/** A keyboard-like event carrying the modifier flags `getStepAmount` reads. */
interface StepEventLike {
  altKey?: boolean
  shiftKey?: boolean
}

/** Converts the `value` attribute (a numeric string) to `number | null`. */
function valueConverter(attr: string | null): number | null {
  if (attr == null || attr.trim() === '') {
    return null
  }
  const num = Number(attr)
  return Number.isNaN(num) ? null : num
}

/** A step must be a finite, strictly-positive number; otherwise fall back. */
function sanitizeStep(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback
}

/**
 * Converts the `step` attribute, keeping the literal `'any'` but coercing
 * numbers. Rejects NaN / non-positive values, falling back to `1`, since a
 * zero or negative step makes stepping (and snap-on-step) meaningless.
 */
function stepConverter(attr: string | null): number | 'any' {
  if (attr == null) {
    return 1
  }
  if (attr === 'any') {
    return 'any'
  }
  return sanitizeStep(Number(attr), 1)
}

/**
 * Number Field is a control for entering a numeric value, with decrement and
 * increment stepper buttons, optional drag-to-scrub, and locale-aware
 * formatting. It ports the behaviour of Base UI's Number Field: arrow/Page
 * stepping, Home/End to the bounds, press-and-hold auto-repeat, optional
 * wheel/scrub, clamping, and `Intl.NumberFormat` formatting.
 *
 * Number Field is a control only — it has no built-in label, hint or error.
 * Compose it inside a [Field](/components/field/) with a
 * [Field Label](/components/field-label/) to give it a label, hint and error,
 * the same way you would a native input.
 *
 * FORM PARTICIPATION: the value is submitted as the raw numeric string under the
 * host `name` via `FormDataController` (the inner input has no `name`, avoiding a
 * double submit). Unlike Base UI there is no hidden native number input, and —
 * like the other new Nord controls — Number Field does NOT use
 * `ElementInternals`, so it performs no native constraint validation: `required`,
 * `min`, `max` and `step` are NOT enforced as form-blocking validity. `min`/`max`
 * govern interactive stepping and (unless `allow-out-of-range` is set) clamp the
 * value; `required` only surfaces as `required`/`aria-required` on the inner input
 * for assistive technology. Authors must validate constraints themselves and
 * reflect failures via `invalid` and a `nord-field-error`.
 *
 * ACCESSIBILITY NOTE: the inner control is a text input (with `inputmode`) and
 * `aria-roledescription="Number field"`, NOT `role="spinbutton"` and NOT a
 * native `type="number"`. A text input lets us show locale-formatted text
 * (currency, percent, grouping) while keeping full text editing — assistive tech
 * reads the formatted value from the input's text. We do NOT add
 * `aria-valuenow`/`valuemin`/`valuemax`/`valuetext`: those are only valid on a
 * range role (e.g. `spinbutton`/`slider`) and would fail `aria-allowed-attr`
 * validation on a text input. This mirrors what Base UI's `NumberFieldInput`
 * actually ships (only `aria-roledescription`).
 *
 * @status draft
 * @category form
 * @lightDom true
 *
 * @fires {NumberFieldChangeEvent} change - Dispatched when the value is committed (on blur, keyboard step, or pointer-up after a button hold or scrub). Carries the numeric value and a `reason`.
 * @fires {NordEvent} input - Dispatched as the value changes during interaction (typing a parseable value, clearing, each step, each button tick, each wheel notch, each scrub increment).
 *
 * @slot start - Leading content inside the control, before the input (e.g. a unit or currency symbol). Sits after the decrement stepper button.
 * @slot end - Trailing content inside the control, after the input (e.g. a unit label or `%`). Sits before the increment stepper button.
 *
 * @localization increaseLabel - Accessible label for the increment stepper button.
 * @localization decreaseLabel - Accessible label for the decrement stepper button.
 * @localization roleDescription - Role description announced for the input (aria-roledescription).
 *
 * @cssprop [--n-number-field-control-min-inline-size=8ch] - Minimum inline size (width) of the numeric input portion of the control.
 * @cssprop [--n-number-field-step-inline-size=var(--n-space-xl)] - Inline size (width) of each increment/decrement stepper button.
 * @cssprop [--n-number-field-background=var(--n-input-background, var(--n-color-surface))] - Background of the control.
 * @cssprop [--n-number-field-border-color=var(--n-input-border-color, var(--n-color-border-strong))] - Border color of the control.
 * @cssprop [--n-number-field-border-radius=var(--n-input-border-radius, var(--n-border-radius-s))] - Corner radius of the control.
 *
 * @attr {boolean} data-invalid - Present while the field is invalid (mirrors the `invalid` property). Style hook for the whole control.
 * @attr {boolean} data-focused - Present while the control has focus. Style hook for focus-within treatments.
 */
@customElement('nord-number-field')
export default class NumberField extends DraftComponentMixin(SizeMixin(FieldControlMixin(LitElement))) {
  /** Stable, unique id base for this instance's light-DOM ARIA wiring. */
  private numberFieldId = `nord-number-field-${numberFieldCount++}`

  // The built-in `<label for>` (from FieldControlMixin) must point at the real
  // focusable input, so override the mixin's default id with the input's id.
  protected override inputId = `${this.numberFieldId}-input`

  private localize = new LocalizeController<'nord-number-field'>(this)

  private inputRef = createRef<HTMLInputElement>()

  // NumberField's `value` is `number | null`, which doesn't overlap with the
  // controller's `InputMixinInterface` host type (`value: string`). The
  // controller only reads `form`/`name`/`disabled` and the `value()` option (not
  // `host.value`), so casting the host through `unknown` is safe — same approach
  // as the combobox, which widens rather than narrows `value`.
  private formData = new FormDataController(
    this as unknown as ReactiveControllerHost & InputMixinInterface,
    { value: () => (this.value == null ? undefined : String(this.value)) },
  )

  /** Reflects state to Base-UI-style `data-*` attributes (data-invalid, data-focused). */
  private stateAttributes = new StateAttributesController(
    this,
    () => ({ invalid: this.effectiveInvalid }),
    { trackFocus: true },
  )

  // The control's own invalid state for the field context: its `invalid` prop or
  // a built-in `error` (when used standalone).
  protected override get ownInvalid() {
    return this.invalid || super.ownInvalid
  }

  /** Re-renders when the host's forwarded ARIA labelling attributes change. */
  private ariaObserver?: MutationObserver

  // Landing zones for relocated `slot="start"`/`slot="end"` host children. The
  // control renders into light DOM, so real `<slot>` projection isn't available.
  @query('.n-number-field-slot-start') private startZone?: HTMLElement
  @query('.n-number-field-slot-end') private endZone?: HTMLElement

  // Watches for `slot="start"`/`slot="end"` children being added or removed so
  // they get relocated into their landing zones on the next render.
  private slotObserver?: MutationObserver

  // ----- Form association (own implementation, mirroring InputMixin) ----------
  // NumberField does NOT use InputMixin because it diverges from `value: string`;
  // it provides its own `name`/`disabled`/`form` so the FormDataController host
  // shape (name/disabled/form) is satisfied.

  private formAncestor: HTMLFormElement | null = null
  private _formId?: string

  /**
   * The raw numeric value of the field, or `null` when empty.
   *
   * The `value` attribute may be set as a numeric string in HTML; it is coerced
   * with a custom converter (`Number(v)`, `NaN -> null`).
   */
  @property({ converter: valueConverter }) value: number | null = null

  /**
   * The minimum allowed value. Step interactions and blur-clamping clamp to it,
   * and the decrement button disables at/below it. Home jumps to it only when it
   * is finite.
   */
  @property({ reflect: true, type: Number }) min?: number

  /**
   * The maximum allowed value. Step interactions and blur-clamping clamp to it,
   * and the increment button disables at/above it. End jumps to it only when it
   * is finite.
   */
  @property({ reflect: true, type: Number }) max?: number

  /**
   * Amount each arrow press / button click / wheel notch changes the value.
   * `'any'` disables step semantics (treated as `1` internally for stepping).
   */
  @property({ reflect: true, converter: stepConverter }) step: number | 'any' = 1

  /** Step used for PageUp/PageDown and Shift+Arrow. */
  @property({ reflect: true, type: Number, attribute: 'large-step' }) largeStep = 10

  /** Step used when Alt/Option is held with Arrow keys. */
  @property({ reflect: true, type: Number, attribute: 'small-step' }) smallStep = 0.1

  /** When `true`, stepping snaps the value to the nearest multiple of the step. */
  @property({ reflect: true, type: Boolean, attribute: 'snap-on-step' }) snapOnStep = false

  /**
   * When `true`, direct text entry may go outside `min`/`max` without clamping
   * (so range validation can flag it); step interactions still clamp.
   */
  @property({ reflect: true, type: Boolean, attribute: 'allow-out-of-range' }) allowOutOfRange = false

  /**
   * Restricts the field to whole numbers. The decimal separator can no longer be
   * typed, pasted or programmatic fractional values are rounded to the nearest
   * integer, and the built-in `mask` caps the fraction to zero. The soft keyboard
   * shows the plain numeric keypad.
   */
  @property({ reflect: true, type: Boolean }) integer = false

  /** When `true`, scrolling the mouse wheel while focused changes the value. */
  @property({ reflect: true, type: Boolean, attribute: 'allow-wheel-scrub' }) allowWheelScrub = false

  /** When `true`, renders a drag handle so the user can click-drag to change the value. */
  @property({ reflect: true, type: Boolean }) scrub = false

  /**
   * `Intl.NumberFormat` options used to format the displayed text. Set as a
   * property only (e.g. `{ style: 'currency', currency: 'EUR' }`).
   */
  @property({ attribute: false }) format?: Intl.NumberFormatOptions

  /**
   * Overrides the default `Intl.NumberFormat`-based formatting of the displayed
   * text. Receives the current numeric value (`null` when the field is empty),
   * the active `locale`, and the `format` options, and must return the string to
   * display. Following the default behaviour, a `null` value should map to an
   * empty string. Set as a property only.
   */
  @property({ attribute: false })
  formatValue?: (value: number | null, locale?: string | string[], options?: Intl.NumberFormatOptions) => string

  /**
   * Overrides the default locale-aware parsing of the typed text. Receives the
   * raw input text, the active `locale`, and the `format` options, and must
   * return the parsed number, or `null` when the text is empty or cannot be
   * parsed. Set as a property only.
   */
  @property({ attribute: false })
  parseValue?: (text: string, locale?: string | string[], options?: Intl.NumberFormatOptions) => number | null

  /**
   * An optional custom edit-time mask: a function that reshapes the input text on
   * every insertion, deletion, and paste, and reports where the caret should
   * land. This is the live-typing counterpart to `formatValue` (which formats an
   * already-committed value). Use it to insert grouping separators, localise the
   * decimal, or cap fraction digits as the user types — typically by wrapping a
   * mask library (e.g. Maskito's `maskitoTransform(elementState, options)`).
   *
   * The field does NOT format live on its own: without a mask, the text is kept
   * as typed and the auto (`Intl`) formatting is applied only on blur and on
   * paste. When a mask is set it becomes the source of truth for the text — the
   * per-character guard is bypassed (the mask decides validity) and paste lands
   * natively so it flows through the mask. Keep the mask output parseable in, and
   * consistent with, the active `locale`/`format`. Set as a property.
   */
  @property({ attribute: false })
  mask?: NumberFieldMaskInput

  /** BCP-47 locale(s) for `Intl` formatting/parsing. Defaults to the runtime locale. */
  @property({ reflect: true }) locale?: string | string[]

  /** Placeholder text shown when the field is empty. */
  @property({ reflect: true }) placeholder?: string

  /** Form field name. The value is submitted under this name via FormDataController. */
  @property({ reflect: true }) name?: string

  /** Disables the control. */
  @property({ reflect: true, type: Boolean }) disabled = false

  /** Read-only: the user cannot change the value, but the value is still submitted. */
  @property({ reflect: true, type: Boolean }) readonly = false

  /** Applies error styling and sets `aria-invalid="true"` on the inner input. */
  @property({ reflect: true, type: Boolean }) invalid = false

  /** Hides the increment/decrement stepper buttons (still steppable via keyboard). */
  @property({ reflect: true, type: Boolean, attribute: 'hide-controls' }) hideControls = false

  /**
   * ISO 4217 currency code (e.g. `USD`, `EUR`, `SEK`). Setting it enables the
   * currency convenience: a decorative, locale-aware symbol is rendered into the
   * `start`/`end` slot resolved from `currencyPlacement`, and the currency is
   * folded into the input's accessible description so assistive tech announces it.
   *
   * This is layered on top of the generic `start`/`end` slots — for a fully
   * custom symbol, omit `currency` and slot your own content instead. It is
   * independent of `format`: the symbol lives beside the number rather than in
   * the formatted text, so it composes cleanly with `mask`.
   */
  @property({ reflect: true }) currency?: string

  /**
   * Which side the `currency` symbol is placed on. `'auto'` (the default)
   * derives the side from the `locale`/`currency` pair via `Intl` (symbol-first
   * locales like en-US → `start`, symbol-last locales like de-DE → `end`);
   * `'start'`/`'end'` force the side. Has no effect unless `currency` is set.
   */
  @property({ reflect: true, attribute: 'currency-placement' }) currencyPlacement: 'start' | 'end' | 'auto' = 'auto'

  /** The text shown in the input — formatted, unless the user is mid-edit. */
  @state() private inputText = ''

  /** Whether the user is scrubbing (adds an `is-scrubbing` cursor). */
  @state() private scrubbing = false

  /**
   * While the user is actively typing this is `false`, so we don't reformat
   * mid-keystroke. Reset to `true` on blur / keydown-step / button / wheel / scrub.
   */
  private allowInputSync = true

  /** Set after the first focus so we only move the caret to the end once. */
  private hasTouchedInput = false

  /**
   * Caret/selection a mask asked us to place after it rewrote the text. Applied
   * in `updated()` once Lit has committed the new `.value` (which resets the
   * caret to the end).
   */
  private pendingSelection?: [number, number]

  // ----- press-and-hold timers ----------------------------------------------
  private holdStartTimer?: ReturnType<typeof setTimeout>
  private holdTickTimer?: ReturnType<typeof setInterval>
  private activePointerId?: number
  /** When a mouse pointerdown handled the change, suppress the synthetic click. */
  private skipNextClick = false

  // ----- scrub state ---------------------------------------------------------
  private scrubAccumulator = 0
  private scrubMoved = false
  private scrubPointerDownTarget: EventTarget | null = null
  private onScrubMove = (e: PointerEvent) => this.handleScrubMove(e)
  private onScrubUp = (e: PointerEvent) => this.handleScrubUp(e)

  /** Bound non-passive wheel listener (attached only while wheel scrub is enabled). */
  private onWheel = (e: WheelEvent) => this.handleWheel(e)
  private wheelAttached = false

  // ----- form-id getter/setter (copied from InputMixin) ----------------------
  set form(id: string) {
    const oldVal = this._formId
    this._formId = id
    this.requestUpdate('form', oldVal)
  }

  @property()
  get form(): HTMLFormElement | null {
    if (this._formId) {
      const root = this.getRootNode() as ShadowRoot | Document
      return root.querySelector<HTMLFormElement>(`form#${this._formId}`)
    }
    return this.formAncestor
  }

  /** The effective lower bound for clamping, mirroring Base UI `minWithDefault`. */
  private get minWithDefault(): number {
    return this.min ?? Number.MIN_SAFE_INTEGER
  }

  /** The effective upper bound for clamping, mirroring Base UI `maxWithDefault`. */
  private get maxWithDefault(): number {
    return this.max ?? Number.MAX_SAFE_INTEGER
  }

  /** The base used by `incrementValue` when stepping from empty. */
  private get minWithZeroDefault(): number {
    return this.min ?? 0
  }

  /**
   * Whether a negative value is allowed, gating the minus sign in the input. A
   * `min` of `0` or higher forbids negatives; an unset or negative `min` allows
   * them (mirroring `minWithDefault`, which backs unset `min` with a value < 0).
   */
  private get allowsNegative(): boolean {
    return this.minWithDefault < 0
  }

  /**
   * The numeric step used for stepping; `'any'` behaves as `1`. Guards against a
   * non-positive / NaN `step` set directly as a property (which bypasses the
   * attribute converter) by falling back to `1`.
   */
  private get numericStep(): number {
    return this.step === 'any' ? 1 : sanitizeStep(this.step, 1)
  }

  /** The PageUp/PageDown + Shift step, guarded against non-positive / NaN values. */
  private get effectiveLargeStep(): number {
    return sanitizeStep(this.largeStep, 10)
  }

  /** The Alt/Option step, guarded against non-positive / NaN values. */
  private get effectiveSmallStep(): number {
    return sanitizeStep(this.smallStep, 0.1)
  }

  /**
   * The canonical displayed text for a value: the `formatValue` override if set,
   * otherwise the locale `Intl` format. Applied on blur and on paste.
   */
  private formatDisplay(value: number | null): string {
    if (this.formatValue) {
      return this.formatValue(value, this.locale, this.format)
    }
    return formatNumber(value, this.locale, this.format)
  }

  connectedCallback() {
    super.connectedCallback()
    injectLegacyLightDomStyles(this, style)

    // Cache the form ancestor; it only changes on (re)connection.
    this.formAncestor = this.closest('form')

    // An external label (e.g. nord-field-label) names the control by setting
    // aria-label / aria-labelledby on the host; forward it to the inner input.
    this.ariaObserver ??= new MutationObserver(() => this.requestUpdate())
    this.ariaObserver.observe(this, {
      attributes: true,
      attributeFilter: ['aria-label', 'aria-labelledby', 'aria-describedby'],
    })

    // Relocate `slot="start"`/`slot="end"` children when they are added or
    // removed; the control is light DOM, so there are no real `<slot>` elements.
    this.slotObserver ??= new MutationObserver(() => this.requestUpdate())
    this.slotObserver.observe(this, { childList: true })

    // Format the initial value if one was set before connect.
    if (this.allowInputSync) {
      this.inputText = this.formatDisplay(this.value)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.ariaObserver?.disconnect()
    this.ariaObserver = undefined
    this.slotObserver?.disconnect()
    this.slotObserver = undefined
    this.formAncestor = null
    this.stopHold()
    this.detachWheel()
    this.removeScrubListeners()
  }

  // Render in the light DOM so the control participates in the document tree
  // (form labelling, Tailwind), like the other new Nord components, but into a
  // layout-transparent (`display: contents`) wrapper rather than the host
  // itself, so slotted `slot="start"`/`slot="end"` children can live as
  // untouched siblings instead of being wiped by this element's own render
  // (mirrors `nord-filter-input` / `nord-combobox`). On the server there is no
  // DOM to append to, so render into the host directly.
  protected createRenderRoot() {
    if (isServer) {
      return this
    }
    const root = document.createElement('div')
    root.className = 'n-number-field-root'
    this.appendChild(root)
    return root
  }

  /**
   * Relocates the host's `slot="start"`/`slot="end"` children into the matching
   * landing zone inside the control. Only direct host children are considered,
   * and a child already sitting in its zone is left untouched, so repeated
   * updates are idempotent and don't disturb focus, caret or pointer state.
   */
  private relocateSlots() {
    if (isServer) {
      return
    }

    const move = (name: 'start' | 'end', target?: HTMLElement) => {
      if (!target) {
        return
      }
      for (const child of Array.from(this.children)) {
        if (child.getAttribute('slot') === name && child.parentElement !== target) {
          target.appendChild(child)
        }
      }
    }

    move('start', this.startZone)
    move('end', this.endZone)
  }

  protected willUpdate(changed: PropertyValues<this>) {
    // A programmatic `value` set (the property changing while the user is NOT
    // mid-edit) must respect the bounds when out-of-range entry isn't allowed.
    // Interactive clamping is handled in `setValue`, which always runs with
    // `allowInputSync === false` during typing; gating on `allowInputSync` here
    // means we only clamp non-interactive sources and never fight live input.
    if (
      changed.has('value')
      && this.allowInputSync
      && !this.allowOutOfRange
      && this.value != null
    ) {
      const clamped = clamp(this.value, this.minWithDefault, this.maxWithDefault)
      if (clamped !== this.value) {
        this.value = clamped
      }
    }

    // A whole-number field never holds a fraction; round any that slips in via a
    // programmatic set or a pasted decimal so the model stays integral.
    if (changed.has('value') && this.integer && this.value != null && !Number.isInteger(this.value)) {
      this.value = Math.round(this.value)
    }

    // Keep the displayed text in sync when the value or formatting changes
    // programmatically, but never overwrite text the user is actively editing.
    if (
      this.allowInputSync
      && (changed.has('value') || changed.has('format') || changed.has('locale'))
    ) {
      this.inputText = this.formatDisplay(this.value)
    }
  }

  protected updated(changed: PropertyValues) {
    super.updated(changed)

    // Move any `slot="start"`/`slot="end"` host children into their landing
    // zones inside the control. The control is light DOM, so real `<slot>`
    // projection isn't available; relocating keeps the public API like `nord-input`.
    this.relocateSlots()

    // Attach or detach the non-passive wheel listener as conditions change.
    const wheelWanted = this.allowWheelScrub && !this.disabled && !this.readonly
    if (wheelWanted && !this.wheelAttached) {
      this.attachWheel()
    }
    else if (!wheelWanted && this.wheelAttached) {
      this.detachWheel()
    }

    // A mask rewrote the text this cycle; Lit has now committed the new `.value`
    // (which parks the caret at the end), so restore the caret/selection the
    // mask requested. Runs in `updated` (before paint) so there is no flash.
    if (this.pendingSelection) {
      const [start, end] = this.pendingSelection
      this.pendingSelection = undefined
      this.inputRef.value?.setSelectionRange(start, end)
    }
  }

  /** Focus the inner input (mirrors combobox `focus`). */
  focus(options?: FocusOptions) {
    this.inputRef.value?.focus(options)
  }

  // ---------------------------------------------------------------------------
  // value model
  // ---------------------------------------------------------------------------

  /** The reasons that DON'T clamp when `allowOutOfRange` is set (direct text entry). */
  private isTextReason(reason: NumberFieldChangeReason): boolean {
    return (
      reason === 'input-change'
      || reason === 'input-blur'
      || reason === 'input-paste'
      || reason === 'input-clear'
      || reason === 'none'
    )
  }

  /**
   * The amount a single step changes the value, mirroring Base UI `getStepAmount`:
   * Alt -> smallStep, Shift -> largeStep, otherwise step.
   */
  private getStepAmount(event?: StepEventLike): number {
    if (event?.altKey) {
      return this.effectiveSmallStep
    }
    if (event?.shiftKey) {
      return this.effectiveLargeStep
    }
    return this.numericStep
  }

  /**
   * Validate, store, format and announce a new value.
   *
   * Returns whether a `change`/value update fired so callers can mirror Base UI's
   * commit semantics.
   */
  private setValue(
    unvalidated: number | null,
    {
      reason,
      direction,
      event,
    }: { reason: NumberFieldChangeReason, direction?: 1 | -1, event?: StepEventLike },
  ): boolean {
    // Only direct text entry may stay out of range when allowOutOfRange is set;
    // step interactions (keyboard/buttons/wheel/scrub) always clamp.
    const shouldClamp = !this.allowOutOfRange || !this.isTextReason(reason)

    const validated = toValidatedNumber(unvalidated, {
      step: direction ? this.getStepAmount(event) * direction : undefined,
      format: this.format,
      minWithDefault: this.minWithDefault,
      maxWithDefault: this.maxWithDefault,
      minWithZeroDefault: this.minWithZeroDefault,
      snapOnStep: this.snapOnStep,
      small: event?.altKey ?? false,
      clamp: shouldClamp,
    })

    const isTextReason = this.isTextReason(reason)
    const shouldFire
      = validated !== this.value
        || (isTextReason && (unvalidated !== this.value || this.allowInputSync === false))

    if (shouldFire) {
      this.value = validated
      // Fired as the value changes during interaction.
      this.dispatchEvent(new NordEvent('input'))
    }

    // Keep the visible input in sync immediately for programmatic/step changes;
    // during direct typing we gate on allowInputSync so we don't clobber the text.
    if (this.allowInputSync) {
      this.inputText = this.formatDisplay(validated)
    }

    this.requestUpdate()
    return shouldFire
  }

  /**
   * Step the value by `amount * direction`, mirroring Base UI `incrementValue`.
   * Stepping from empty starts at `max(0, min ?? 0)`.
   */
  private incrementValue(
    amount: number,
    {
      direction,
      currentValue,
      event,
      reason,
    }: {
      direction: 1 | -1
      currentValue?: number | null
      event?: StepEventLike
      reason: NumberFieldChangeReason
    },
  ): boolean {
    const prev = currentValue == null ? this.value : currentValue
    const next
      = typeof prev === 'number' ? prev + amount * direction : Math.max(0, this.min ?? 0)
    return this.setValue(next, { reason, direction, event })
  }

  /** Dispatch the committed `change` event with the current value + reason. */
  private commit(reason: NumberFieldChangeReason) {
    this.dispatchEvent(new NumberFieldChangeEvent(this.value, reason))
  }

  // ---------------------------------------------------------------------------
  // keyboard
  // ---------------------------------------------------------------------------

  /**
   * The set of non-numeric keys allowed in the input, mirroring Base UI's
   * `getAllowedNonNumericKeys` (locale decimal/group/currency/percent + signs).
   */
  private getAllowedNonNumericKeys(): Set<string> {
    // This guard only runs when no mask is active (`mask="off"`); the built-in and
    // custom masks decide validity themselves and bypass it.
    const { decimal, group, currency, literal } = getNumberLocaleDetails(this.locale, this.format)
    const keys = new Set<string>()

    BASE_NON_NUMERIC_SYMBOLS.forEach(symbol => keys.add(symbol))
    if (decimal) {
      keys.add(decimal)
    }
    if (group) {
      keys.add(group)
      if (SPACE_SEPARATOR_RE.test(group)) {
        keys.add(' ')
      }
    }

    const formatStyle = this.format?.style
    const allowPercent
      = formatStyle === 'percent' || (formatStyle === 'unit' && this.format?.unit === 'percent')
    const allowPermille
      = formatStyle === 'percent' || (formatStyle === 'unit' && this.format?.unit === 'permille')

    if (allowPercent) {
      PERCENTAGES.forEach(key => keys.add(key))
    }
    if (allowPermille) {
      PERMILLE.forEach(key => keys.add(key))
    }
    if (formatStyle === 'currency' && currency) {
      keys.add(currency)
    }
    if (literal) {
      Array.from(literal).forEach(char => keys.add(char))
      if (SPACE_SEPARATOR_RE.test(literal)) {
        keys.add(' ')
      }
    }

    PLUS_SIGNS_WITH_ASCII.forEach(key => keys.add(key))
    if (this.minWithDefault < 0) {
      MINUS_SIGNS_WITH_ASCII.forEach(key => keys.add(key))
    }

    // A whole-number field drops every decimal separator (the locale's own plus
    // the universal ASCII/fullwidth/Arabic glyphs that `BASE_NON_NUMERIC_SYMBOLS`
    // seeds), but keeps the group separator so grouped input still parses — even
    // where the group char (e.g. de-DE `.`) coincides with a decimal glyph.
    if (this.integer) {
      for (const separator of [decimal, '.', '．', '٫']) {
        if (separator && separator !== group) {
          keys.delete(separator)
        }
      }
    }

    return keys
  }

  private handleKeydown(e: KeyboardEvent) {
    if (e.defaultPrevented || this.readonly || this.disabled) {
      return
    }

    this.allowInputSync = true

    const stepKeys = new Set(['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End'])

    if (!stepKeys.has(e.key)) {
      // A custom mask governs which characters are valid and reshapes the text on
      // the `input` event, so let every key through when one is set.
      if (this.mask) {
        return
      }

      // Let typing through, but reject characters that can never be part of a
      // number (porting Base UI's allowed-key guard for parity). Composition
      // (which===229), navigation keys, and modifier combos always pass.
      const allowed = this.getAllowedNonNumericKeys()
      const isAsciiDigit = e.key >= '0' && e.key <= '9'
      const isNamedKey = e.key.length > 1 // ArrowLeft, Backspace, Tab, Enter, …
      const passes
        = e.which === 229
          || e.ctrlKey
          || e.metaKey
          || (e.altKey)
          || isAsciiDigit
          || isNamedKey
          || allowed.has(e.key)
          || ARABIC_DETECT_RE.test(e.key)
          || PERSIAN_DETECT_RE.test(e.key)
          || HAN_DETECT_RE.test(e.key)
          || FULLWIDTH_DETECT_RE.test(e.key)
          || (this.allowsNegative && ANY_MINUS_DETECT_RE.test(e.key))
          || ANY_PLUS_DETECT_RE.test(e.key)

      if (!passes) {
        e.preventDefault()
      }
      return
    }

    // Parse the current (possibly unblurred) text so edits are honoured.
    const currentValue = this.parseValue
      ? this.parseValue(this.inputText, this.locale, this.format)
      : parseNumber(this.inputText, this.locale, this.format)

    switch (e.key) {
      case 'ArrowUp':
        e.preventDefault()
        this.incrementValue(this.getStepAmount(e), {
          direction: 1,
          currentValue,
          event: e,
          reason: 'keyboard',
        })
        this.commit('keyboard')
        break
      case 'ArrowDown':
        e.preventDefault()
        this.incrementValue(this.getStepAmount(e), {
          direction: -1,
          currentValue,
          event: e,
          reason: 'keyboard',
        })
        this.commit('keyboard')
        break
      case 'PageUp':
        e.preventDefault()
        // PageUp/Down always use largeStep regardless of modifiers.
        this.incrementValue(this.effectiveLargeStep, { direction: 1, currentValue, reason: 'keyboard' })
        this.commit('keyboard')
        break
      case 'PageDown':
        e.preventDefault()
        this.incrementValue(this.effectiveLargeStep, { direction: -1, currentValue, reason: 'keyboard' })
        this.commit('keyboard')
        break
      case 'Home':
        if (this.min != null && Number.isFinite(this.min)) {
          e.preventDefault()
          this.setValue(this.min, { reason: 'keyboard' })
          this.commit('keyboard')
        }
        break
      case 'End':
        if (this.max != null && Number.isFinite(this.max)) {
          e.preventDefault()
          this.setValue(this.max, { reason: 'keyboard' })
          this.commit('keyboard')
        }
        break
      default:
        break
    }
  }

  // ---------------------------------------------------------------------------
  // input / paste / focus / blur
  // ---------------------------------------------------------------------------

  /**
   * Whether every character in `text` could legitimately be part of a number in
   * the current locale/format, mirroring Base UI `NumberFieldInput.onChange`'s
   * `isValidCharacterString`. The keydown guard only sees physical keystrokes;
   * this catches disallowed characters arriving via IME composition,
   * drag-and-drop, or a programmatic `input.value` set, so they never land in
   * the bound value.
   */
  private isValidCharacterString(text: string): boolean {
    const allowed = this.getAllowedNonNumericKeys()
    return Array.from(text).every((ch) => {
      const isAsciiDigit = ch >= '0' && ch <= '9'
      return (
        isAsciiDigit
        || ARABIC_DETECT_RE.test(ch)
        || HAN_DETECT_RE.test(ch)
        || PERSIAN_DETECT_RE.test(ch)
        || FULLWIDTH_DETECT_RE.test(ch)
        || (this.allowsNegative && ANY_MINUS_DETECT_RE.test(ch))
        || allowed.has(ch)
      )
    })
  }

  private handleInput(e: Event) {
    e.stopPropagation()

    // While typing, don't reformat the text from underneath the user.
    this.allowInputSync = false

    const input = e.target as HTMLInputElement
    let text = input.value

    // A custom mask, when set, reshapes the raw text (grouping separators,
    // decimal-separator localisation, fraction cap, …) and tells us where the
    // caret should land afterwards.
    const mask = this.mask
    if (mask && text.trim() !== '') {
      const masked = mask(
        {
          value: text,
          selectionStart: input.selectionStart ?? text.length,
          selectionEnd: input.selectionEnd ?? text.length,
        },
        { locale: this.locale, format: this.format },
      )
      text = masked.value
      this.pendingSelection = [masked.selectionStart, masked.selectionEnd]
    }

    if (text.trim() === '') {
      this.inputText = ''
      this.pendingSelection = undefined
      this.setValue(null, { reason: 'input-clear' })
      return
    }

    // Reject input that contains characters that can never be part of a number
    // (composition / drag-drop / programmatic sets bypass the keydown guard);
    // bail before committing so disallowed characters never reach the value. A
    // mask governs character validity itself, so skip the generic guard for it.
    if (!this.mask && !this.isValidCharacterString(text)) {
      return
    }

    this.inputText = text
    const parsed = this.parseValue
      ? this.parseValue(text, this.locale, this.format)
      : parseNumber(text, this.locale, this.format)
    if (parsed !== null) {
      this.setValue(parsed, { reason: 'input-change' })
    }
  }

  private handlePaste(e: ClipboardEvent) {
    if (e.defaultPrevented || this.readonly || this.disabled) {
      return
    }

    // With a custom mask, let the paste land natively; the resulting `input`
    // event runs through the mask, which reshapes pasted text like any insertion.
    if (this.mask) {
      return
    }

    const pasted = e.clipboardData?.getData('text/plain') ?? ''
    // Prevent the native change so we control the parsed text.
    e.preventDefault()

    const parsed = this.parseValue
      ? this.parseValue(pasted, this.locale, this.format)
      : parseNumber(pasted, this.locale, this.format)
    if (parsed !== null) {
      // Apply the auto (Intl) formatting to pasted content immediately, the same
      // as on blur. `allowInputSync` lets `setValue` refresh the displayed text.
      this.allowInputSync = true
      this.setValue(parsed, { reason: 'input-paste' })
    }
  }

  private handleFocus(e: FocusEvent) {
    if (e.defaultPrevented || this.readonly || this.disabled) {
      return
    }

    if (this.hasTouchedInput) {
      return
    }
    this.hasTouchedInput = true

    // Browsers select the start by default; move the caret to the end on first focus.
    const target = e.currentTarget as HTMLInputElement
    const length = target.value.length
    target.setSelectionRange(length, length)
  }

  private handleBlur(e: FocusEvent) {
    if (e.defaultPrevented || this.readonly || this.disabled) {
      return
    }

    this.allowInputSync = true

    if (this.inputText.trim() === '') {
      this.setValue(null, { reason: 'input-clear' })
      this.commit('input-clear')
      return
    }

    const parsed = this.parseValue
      ? this.parseValue(this.inputText, this.locale, this.format)
      : parseNumber(this.inputText, this.locale, this.format)
    if (parsed === null) {
      // The typed text can't be parsed — e.g. stray signs or decimals the
      // lenient per-character guard let through ("--.-1,234"). Rather than leave
      // the garbage sitting in the field, revert the display to the last
      // committed value (its canonical format, or empty when there is none). The
      // value itself is unchanged, so no change event fires.
      const reverted = this.formatDisplay(this.value)
      if (this.inputText !== reverted) {
        this.inputText = reverted
      }
      return
    }

    const hasRounding = hasNumberFormatRoundingOptions(this.format)
    const committed = hasRounding ? removeFloatingPointErrors(parsed, this.format) : parsed

    const shouldUpdate = committed !== this.value
    if (shouldUpdate) {
      this.setValue(committed, { reason: 'input-blur' })
    }

    // Normalise the displayed text to the canonical formatted value.
    const canonical = this.formatDisplay(this.value)
    if (this.inputText !== canonical) {
      this.inputText = canonical
    }

    this.commit('input-blur')
  }

  // ---------------------------------------------------------------------------
  // stepper buttons (press-and-hold)
  // ---------------------------------------------------------------------------

  /** Sync the (possibly dirty) input text into `value` before a button press. */
  private commitDirtyInput(reason: NumberFieldChangeReason, direction: 1 | -1) {
    this.allowInputSync = true
    const parsed = this.parseValue
      ? this.parseValue(this.inputText, this.locale, this.format)
      : parseNumber(this.inputText, this.locale, this.format)
    if (parsed !== null) {
      this.setValue(parsed, { reason, direction })
    }
  }

  private handleStepPointerDown(e: PointerEvent, direction: 1 | -1) {
    const isMainButton = !e.button || e.button === 0
    if (e.defaultPrevented || this.readonly || this.disabled || !isMainButton) {
      return
    }

    const reason: NumberFieldChangeReason
      = direction === 1 ? 'increment-press' : 'decrement-press'

    // Pointerdown handles the change here, so suppress the follow-up click
    // (including the synthetic click browsers fire after touch).
    this.skipNextClick = true
    this.activePointerId = e.pointerId

    // Sync the dirty input value before stepping.
    this.commitDirtyInput(reason, direction)

    if (e.pointerType !== 'touch' && e.pointerType !== 'pen') {
      // Focus the input so keyboard interaction can continue.
      this.inputRef.value?.focus()
    }

    // One immediate step, then auto-repeat after the start delay.
    this.allowInputSync = true
    this.incrementValue(this.getStepAmount(e), { direction, event: e, reason })

    const target = e.currentTarget as HTMLButtonElement
    try {
      target.setPointerCapture(e.pointerId)
    }
    catch {
      // Ignore: capture isn't essential, pointerup is still observed.
    }

    this.holdStartTimer = setTimeout(() => {
      this.holdTickTimer = setInterval(() => {
        this.allowInputSync = true
        this.incrementValue(this.getStepAmount(e), { direction, event: e, reason })
      }, CHANGE_VALUE_TICK_DELAY)
    }, START_AUTO_CHANGE_DELAY)
  }

  private handleStepPointerStop(e: PointerEvent, direction: 1 | -1) {
    // Ignore stop events from a different pointer than the one that started.
    if (this.activePointerId !== undefined && this.activePointerId !== e.pointerId) {
      return
    }
    const wasHolding = this.holdStartTimer != null || this.holdTickTimer != null
    this.stopHold()
    this.activePointerId = undefined

    // Commit on pointer-up if the pointer press drove the change.
    if (this.skipNextClick || wasHolding) {
      this.commit(direction === 1 ? 'increment-press' : 'decrement-press')
    }
  }

  private handleStepClick(e: MouseEvent, direction: 1 | -1) {
    const reason: NumberFieldChangeReason
      = direction === 1 ? 'increment-press' : 'decrement-press'

    if (e.defaultPrevented || this.readonly || this.disabled) {
      return
    }

    // Pointerdown already handled the change; this suppresses the follow-up click.
    if (this.skipNextClick) {
      this.skipNextClick = false
      return
    }

    this.commitDirtyInput(reason, direction)
    this.allowInputSync = true
    const changed = this.incrementValue(this.getStepAmount(e as StepEventLike), {
      direction,
      event: e as StepEventLike,
      reason,
    })
    if (changed) {
      this.commit(reason)
    }
  }

  private stopHold() {
    if (this.holdStartTimer != null) {
      clearTimeout(this.holdStartTimer)
      this.holdStartTimer = undefined
    }
    if (this.holdTickTimer != null) {
      clearInterval(this.holdTickTimer)
      this.holdTickTimer = undefined
    }
  }

  /** Whether the increment button should be disabled. */
  private get incrementDisabled(): boolean {
    return (
      this.disabled
      || this.readonly
      || (this.value != null && this.value >= this.maxWithDefault)
    )
  }

  /** Whether the decrement button should be disabled. */
  private get decrementDisabled(): boolean {
    return (
      this.disabled
      || this.readonly
      || (this.value != null && this.value <= this.minWithDefault)
    )
  }

  // ---------------------------------------------------------------------------
  // wheel
  // ---------------------------------------------------------------------------

  private attachWheel() {
    const input = this.inputRef.value
    if (!input) {
      return
    }
    input.addEventListener('wheel', this.onWheel, { passive: false })
    this.wheelAttached = true
  }

  private detachWheel() {
    const input = this.inputRef.value
    input?.removeEventListener('wheel', this.onWheel)
    this.wheelAttached = false
  }

  private handleWheel(e: WheelEvent) {
    // Allow pinch-zoom (ctrlKey) and ignore when the input isn't focused.
    if (e.ctrlKey || this.inputRef.value !== this.getActiveElement()) {
      return
    }
    e.preventDefault()
    this.allowInputSync = true
    const amount = this.getStepAmount(e as StepEventLike)
    this.incrementValue(amount, {
      direction: e.deltaY > 0 ? -1 : 1,
      event: e as StepEventLike,
      reason: 'wheel',
    })
  }

  /** The active element within this node's root (document or shadow root). */
  private getActiveElement(): Element | null {
    const root = this.getRootNode() as Document | ShadowRoot
    return root.activeElement
  }

  // ---------------------------------------------------------------------------
  // scrub
  //
  // NOTE: this is a SIMPLIFIED port of Base UI's ScrubArea. Pointer Lock, the
  // virtual cursor, and teleport are intentionally OUT OF SCOPE — this is a
  // basic relative drag (movementX * step, gated by pixelSensitivity), like the
  // combobox's "no virtualization" note.
  // ---------------------------------------------------------------------------

  private handleScrubPointerDown(e: PointerEvent) {
    const isMainButton = !e.button || e.button === 0
    if (e.defaultPrevented || this.readonly || this.disabled || !isMainButton) {
      return
    }

    if (e.pointerType === 'mouse') {
      e.preventDefault()
      this.inputRef.value?.focus()
    }

    this.scrubbing = true
    this.scrubMoved = false
    this.scrubAccumulator = 0
    this.scrubPointerDownTarget = e.target

    const win = this.ownerWindow()
    win.addEventListener('pointermove', this.onScrubMove, true)
    win.addEventListener('pointerup', this.onScrubUp, true)
  }

  private handleScrubMove(e: PointerEvent) {
    if (!this.scrubbing) {
      return
    }
    // Prevent text selection while dragging.
    e.preventDefault()

    this.scrubAccumulator += e.movementX

    if (Math.abs(this.scrubAccumulator) >= SCRUB_PIXEL_SENSITIVITY) {
      this.scrubAccumulator = 0
      this.scrubMoved = true
      const dValue = e.movementX
      const amount = Math.abs(dValue * this.getStepAmount(e as StepEventLike))
      if (amount !== 0) {
        this.allowInputSync = true
        this.incrementValue(amount, {
          direction: dValue >= 0 ? 1 : -1,
          event: e as StepEventLike,
          reason: 'scrub',
        })
      }
    }
  }

  private handleScrubUp(e: PointerEvent) {
    this.removeScrubListeners()
    this.scrubbing = false
    this.commit('scrub')

    // If no movement happened, synthesise a click on the original target so a
    // click-without-drag still focuses the input.
    if (!this.scrubMoved && this.scrubPointerDownTarget) {
      this.scrubPointerDownTarget.dispatchEvent(
        new MouseEvent('click', { bubbles: true, cancelable: true }),
      )
    }
    this.scrubMoved = false
    this.scrubPointerDownTarget = null
  }

  private removeScrubListeners() {
    const win = this.ownerWindow()
    win.removeEventListener('pointermove', this.onScrubMove, true)
    win.removeEventListener('pointerup', this.onScrubUp, true)
  }

  private handleScrubTouchStart(e: TouchEvent) {
    if (this.disabled || this.readonly) {
      return
    }
    if (e.touches.length === 1) {
      // Stop the page scrolling while dragging.
      e.preventDefault()
    }
  }

  private ownerWindow(): Window {
    return this.ownerDocument.defaultView ?? window
  }

  // ---------------------------------------------------------------------------
  // render
  // ---------------------------------------------------------------------------

  /**
   * The `inputmode` for the inner input.
   *
   * Mirrors Base UI's `setDynamicInputModeForIOS` intent: the iOS numeric and
   * decimal soft keyboards have no minus key, so when negatives are explicitly
   * allowed (an explicit `min < 0`) we yield `text` so the user can type a
   * minus sign on iOS. Otherwise the base mode is `numeric` when step/min/max
   * are all integers and min >= 0, and `decimal` when fractional values are in
   * play (so the iOS keyboard exposes a decimal point).
   *
   * Base UI swaps to `text`/`decimal` at runtime only on iOS; we compute the
   * mode statically, and `text`/`decimal` are harmless supersets on platforms
   * whose numeric keyboard already has the relevant keys. We gate the `text`
   * mode on the explicit `min` prop (not the `Number.MIN_SAFE_INTEGER`-backed
   * `minWithDefault`) so an unconstrained field keeps the friendlier `numeric`
   * keypad, matching Base UI's default of `numeric`.
   */
  private get computedInputMode(): 'numeric' | 'decimal' | 'text' {
    // iOS numeric/decimal keyboards lack a minus key; fall back to text so a
    // negative value can be entered when negatives are explicitly allowed.
    if (this.min != null && this.min < 0) {
      return 'text'
    }
    // Whole-number fields never accept a decimal point, so the numeric keypad
    // (no decimal key) is always the right soft keyboard.
    if (this.integer) {
      return 'numeric'
    }
    const stepIsInt = this.step === 'any' || Number.isInteger(this.numericStep)
    const minOk = this.min == null || (Number.isInteger(this.min) && this.min >= 0)
    const maxOk = this.max == null || Number.isInteger(this.max)
    return stepIsInt && minOk && maxOk ? 'numeric' : 'decimal'
  }

  /**
   * The resolved side for the `currency` symbol: the explicit `currencyPlacement`
   * when it is `'start'`/`'end'`, otherwise derived from the locale/currency via
   * `Intl`. Only meaningful when `currency` is set.
   */
  private get resolvedCurrencyPlacement(): 'start' | 'end' {
    if (this.currencyPlacement === 'start' || this.currencyPlacement === 'end') {
      return this.currencyPlacement
    }
    return this.currency ? resolveCurrencyPlacement(this.locale, this.currency) : 'start'
  }

  render() {
    const inputId = this.inputId

    // Naming/description from the field context (in a field), the built-in
    // <label for>/hint/error (standalone), or consumer-set host aria-* — all
    // resolved by the FieldControlMixin getters.
    const ariaLabel = this.controlAriaLabel
    const ariaLabelledby = this.controlAriaLabelledby

    // Currency convenience: a decorative, locale-aware symbol placed in the
    // resolved slot. It is `aria-hidden` (the number lives in the input's text),
    // so we fold its id into `aria-describedby` — a directly-referenced element
    // still contributes to the accessible description even when hidden — so
    // assistive tech announces the currency alongside the value. This is the
    // non-destructive analogue of NFE's aria-label composition: it works in every
    // labelling mode (field label, `aria-label`, `label` prop) without clobbering
    // the accessible name.
    const currencySymbol = this.currency ? getCurrencySymbol(this.locale, this.currency) : undefined
    const currencyPlacement = this.currency ? this.resolvedCurrencyPlacement : undefined
    const currencySymbolId = this.currency ? `${inputId}-currency` : undefined
    const ariaDescribedby
      = [this.controlAriaDescribedby, currencySymbolId].filter(Boolean).join(' ') || undefined

    const renderCurrencySymbol = (side: 'start' | 'end') =>
      currencySymbol != null && currencyPlacement === side
        ? html`<span id=${currencySymbolId} class="n-currency-symbol" aria-hidden="true">${currencySymbol}</span>`
        : nothing

    return html`
      ${this.renderLabel()}

      <div
        class=${classMap({
          'n-number-field': true,
          'is-scrubbing': this.scrubbing,
        })}
      >
        ${this.scrub
          ? html`
              <!-- A drag-to-scrub handle. It is purely a pointer affordance
              (role="presentation"); the field's accessible name still comes from
              the external nord-field-label pointed at the input id, so keyboard
              and AT users are unaffected. The grip mirrors Base UI's ew-resize
              cursor icon. -->
              <div
                class="n-scrub"
                role="presentation"
                aria-hidden="true"
                @pointerdown=${this.handleScrubPointerDown}
                @touchstart=${this.handleScrubTouchStart}
              >
                <svg
                  class="n-scrub-grip"
                  width="24"
                  height="14"
                  viewBox="0 0 24 14"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path
                    d="M19.5 5.5L6.49737 5.51844V2L1 6.9999L6.5 12L6.49737 8.5L19.5 8.5V12L25 6.9999L19.5 2V5.5Z"
                  ></path>
                </svg>
              </div>
            `
          : nothing}

        <div class=${classMap({ 'n-control': true, 'is-invalid': this.effectiveInvalid })}>
          ${this.hideControls
            ? nothing
            : html`
                <button
                  class=${classMap({ 'n-step': true, 'n-decrement': true })}
                  type="button"
                  tabindex="-1"
                  aria-label=${this.localize.term('decreaseLabel')}
                  aria-controls=${inputId}
                  aria-readonly=${this.readonly ? 'true' : nothing}
                  ?disabled=${this.decrementDisabled}
                  @pointerdown=${(e: PointerEvent) => this.handleStepPointerDown(e, -1)}
                  @pointerup=${(e: PointerEvent) => this.handleStepPointerStop(e, -1)}
                  @pointerleave=${(e: PointerEvent) => this.handleStepPointerStop(e, -1)}
                  @pointercancel=${(e: PointerEvent) => this.handleStepPointerStop(e, -1)}
                  @click=${(e: MouseEvent) => this.handleStepClick(e, -1)}
                >
                  <nord-icon name="interface-remove-small"></nord-icon>
                </button>
              `}

          <span class="n-number-field-slot n-number-field-slot-start">${renderCurrencySymbol('start')}</span>

          <input
            ${ref(this.inputRef)}
            class="n-input"
            type="text"
            id=${inputId}
            inputmode=${this.computedInputMode}
            .value=${this.inputText}
            placeholder=${this.placeholder ?? nothing}
            ?disabled=${this.effectiveDisabled}
            ?readonly=${this.readonly}
            ?required=${this.required}
            autocomplete="off"
            autocorrect="off"
            spellcheck="false"
            aria-roledescription=${this.localize.term('roleDescription')}
            aria-label=${ariaLabel ?? nothing}
            aria-labelledby=${ariaLabelledby ?? nothing}
            aria-describedby=${ariaDescribedby ?? nothing}
            aria-invalid=${this.controlAriaInvalid ?? nothing}
            aria-required=${this.required ? 'true' : nothing}
            aria-readonly=${this.readonly ? 'true' : nothing}
            @input=${this.handleInput}
            @blur=${this.handleBlur}
            @focus=${this.handleFocus}
            @keydown=${this.handleKeydown}
            @paste=${this.handlePaste}
          />

          <span class="n-number-field-slot n-number-field-slot-end">${renderCurrencySymbol('end')}</span>

          ${this.hideControls
            ? nothing
            : html`
                <button
                  class=${classMap({ 'n-step': true, 'n-increment': true })}
                  type="button"
                  tabindex="-1"
                  aria-label=${this.localize.term('increaseLabel')}
                  aria-controls=${inputId}
                  aria-readonly=${this.readonly ? 'true' : nothing}
                  ?disabled=${this.incrementDisabled}
                  @pointerdown=${(e: PointerEvent) => this.handleStepPointerDown(e, 1)}
                  @pointerup=${(e: PointerEvent) => this.handleStepPointerStop(e, 1)}
                  @pointerleave=${(e: PointerEvent) => this.handleStepPointerStop(e, 1)}
                  @pointercancel=${(e: PointerEvent) => this.handleStepPointerStop(e, 1)}
                  @click=${(e: MouseEvent) => this.handleStepClick(e, 1)}
                >
                  <nord-icon name="interface-add-small"></nord-icon>
                </button>
              `}
        </div>
      </div>

      ${this.renderError()}
      ${this.isHintBelow ? this.renderHint() : nothing}
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-number-field': NumberField
  }
}
